import { type ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArtifactDropOverlay,
  formatDuplicateMessage,
  formatUploadLimitError,
  PendingUploadCard,
  useAdminArtifactUploadConstraints,
  useAdminOrganizationArtifactEventStream,
  useChartWideDropZone,
  useOptimisticUploads,
  useUploadAdminOrganizationArtifacts,
  validateFiles,
} from '@features/patient-artifacts';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';

import { IntakeHubDashboard } from './components/IntakeHubDashboard';
import { intakeHubDashboardResponseToScenario } from './mappers';
import {
  useEnrollAdminIntakeHubServicesMutation,
  useGetAdminIntakeHubDashboardQuery,
  useGetAdminIntakeHubDocumentsQuery,
} from './store/api';
import type { IntakeItem, IntakeServiceId } from './types';

const DEFAULT_SELECTED_SERVICES: IntakeServiceId[] = ['jotcred', 'jotbill'];

interface AdminIntakeHubProvider {
  provider_id?: string;
  user_id?: string;
  name?: string;
  email?: string;
  roles?: string[];
}

interface AdminIntakeHubEmbeddedProps {
  organizationId: string;
  providers?: AdminIntakeHubProvider[];
}

function toServiceOption(service: Record<string, unknown>, index: number) {
  const fallbackIds: IntakeServiceId[] = ['jotcred', 'jotbill', 'jotsite'];
  const id =
    typeof service.service_key === 'string'
      ? (service.service_key as IntakeServiceId)
      : fallbackIds[index];

  return {
    id,
    label: typeof service.label === 'string' ? service.label : id,
    description: typeof service.description === 'string' ? service.description : '',
  };
}

function getProviderId(provider: AdminIntakeHubProvider) {
  return provider.user_id || provider.provider_id || '';
}

export function AdminIntakeHubEmbedded({
  organizationId,
  providers = [],
}: AdminIntakeHubEmbeddedProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingUploadItemRef = useRef<IntakeItem | undefined>(undefined);
  const { enqueueSnackbar } = useSnackbar();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedServiceIds, setSelectedServiceIds] =
    useState<IntakeServiceId[]>(DEFAULT_SELECTED_SERVICES);
  const [uploadingFileCount, setUploadingFileCount] = useState(0);

  const dashboardArgs = {
    organizationId,
    userId: selectedUserId || undefined,
  };
  const { data, error, isFetching, refetch } = useGetAdminIntakeHubDashboardQuery(dashboardArgs, {
    skip: !organizationId,
  });
  const { data: documentInventory, refetch: refetchDocumentInventory } =
    useGetAdminIntakeHubDocumentsQuery(dashboardArgs, {
      skip: !organizationId || !data,
    });
  const [enrollServices, { isLoading: isEnrolling }] = useEnrollAdminIntakeHubServicesMutation();
  const { constraints: uploadConstraints, summary: uploadConstraintsSummary } =
    useAdminArtifactUploadConstraints();
  const { upload: uploadOrganizationFiles } = useUploadAdminOrganizationArtifacts(organizationId);
  const { pendingUploads, trackUpload, handleArtifactEvent, handleDocRefExtraction } =
    useOptimisticUploads();

  const providerOptions = useMemo(
    () =>
      providers
        .filter(provider => getProviderId(provider))
        .map(provider => ({
          id: getProviderId(provider),
          label: provider.name || provider.email || getProviderId(provider),
          email: provider.email,
        })),
    [providers],
  );
  const serviceOptions = useMemo(
    () =>
      (data?.services ?? [])
        .filter(Boolean)
        .map((service, index) => toServiceOption(service as Record<string, unknown>, index)),
    [data?.services],
  );
  const scenario = useMemo(() => intakeHubDashboardResponseToScenario(data), [data]);
  const hasNoEnrollments = Boolean(data) && (data?.enrollments?.length ?? 0) === 0;
  const scopeLabel = selectedUserId
    ? (providerOptions.find(provider => provider.id === selectedUserId)?.label ??
      'Selected provider')
    : 'Organization scope';

  const handleUploadFiles = useCallback(
    async (files: File[], item?: IntakeItem) => {
      if (!organizationId) {
        enqueueSnackbar('Select an organization before uploading intake files.', {
          variant: 'warning',
        });
        return;
      }

      const { accepted, rejected } = validateFiles(files, uploadConstraints);
      for (const rejection of rejected) {
        enqueueSnackbar(rejection.reason, { variant: 'error' });
      }
      if (accepted.length === 0) return;

      setUploadingFileCount(accepted.length);
      let outcome: Awaited<ReturnType<typeof uploadOrganizationFiles>>;
      try {
        const intakeUserId = selectedUserId || data?.clinician?.user_id || undefined;
        outcome = await uploadOrganizationFiles(accepted, {
          sourceChannel: 'intake_hub_admin',
          intakeHub: item
            ? {
                intakeItemId: item.intakeItemId ?? item.id,
                requirementKey: item.requirementKey,
                conceptKey: item.conceptKey,
                serviceKey: item.serviceIds[0],
                userId: intakeUserId,
              }
            : { userId: intakeUserId },
        });
      } catch (uploadError) {
        enqueueSnackbar(formatUploadLimitError(uploadError), { variant: 'error' });
        return;
      } finally {
        setUploadingFileCount(0);
      }

      trackUpload(outcome);
      for (const duplicate of outcome?.duplicates ?? []) {
        enqueueSnackbar(formatDuplicateMessage(duplicate, 'organization'), {
          variant: 'info',
        });
      }
      if (outcome?.artifacts?.length) {
        enqueueSnackbar('Upload received. Intake Hub is extracting the details now.', {
          variant: 'success',
        });
      }
      await refetch();
      await refetchDocumentInventory();
    },
    [
      data?.clinician?.user_id,
      enqueueSnackbar,
      organizationId,
      refetch,
      selectedUserId,
      trackUpload,
      uploadConstraints,
      uploadOrganizationFiles,
    ],
  );

  const handleOpenFilePicker = (item?: IntakeItem) => {
    pendingUploadItemRef.current = item;
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    void handleUploadFiles(files, pendingUploadItemRef.current);
    pendingUploadItemRef.current = undefined;
  };

  const handleArtifactStreamEvent = useCallback(
    (event: Parameters<typeof handleArtifactEvent>[0]) => {
      handleArtifactEvent(event);
      void refetch();
      void refetchDocumentInventory();
    },
    [handleArtifactEvent, refetch, refetchDocumentInventory],
  );

  useAdminOrganizationArtifactEventStream(organizationId, {
    enabled: Boolean(organizationId) && pendingUploads.length > 0,
    onEvent: handleArtifactStreamEvent,
    onDocRefExtraction: event => {
      handleDocRefExtraction(event);
      void refetch();
      void refetchDocumentInventory();
    },
  });

  const { isDragging } = useChartWideDropZone({
    enabled: Boolean(organizationId) && !hasNoEnrollments,
    onDrop: files => void handleUploadFiles(files),
  });
  const isUploadInFlight = uploadingFileCount > 0;

  const handleToggleService = (serviceId: IntakeServiceId) => {
    setSelectedServiceIds(current =>
      current.includes(serviceId)
        ? current.filter(currentServiceId => currentServiceId !== serviceId)
        : [...current, serviceId],
    );
  };

  const handleStartIntake = async () => {
    if (!organizationId || selectedServiceIds.length === 0) return;
    try {
      await enrollServices({
        organizationId,
        userId: selectedUserId || undefined,
        enrollServicesRequest: {
          service_keys: selectedServiceIds,
        },
      }).unwrap();
      await refetch();
    } catch {
      enqueueSnackbar('Unable to start Intake Hub right now. Please try again.', {
        variant: 'error',
      });
    }
  };

  return (
    <Box sx={{ bgcolor: theme.palette.grey[100], borderRadius: 1, overflow: 'hidden' }}>
      <Box
        component="input"
        ref={fileInputRef}
        type="file"
        multiple
        accept={uploadConstraints.allowed_content_types.join(',')}
        onChange={handleFileInputChange}
        sx={{ display: 'none' }}
      />
      {(isDragging || isUploadInFlight) && (
        <ArtifactDropOverlay
          patientName="Intake Hub"
          subtitle={uploadConstraintsSummary}
          isUploading={isUploadInFlight}
          uploadingMessage={`Uploading ${uploadingFileCount} file${
            uploadingFileCount === 1 ? '' : 's'
          }`}
        />
      )}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar,
          bgcolor: alpha(theme.palette.background.paper, 0.9),
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          backdropFilter: 'blur(14px)',
        }}
      >
        {isFetching && <LinearProgress />}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          sx={{
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            px: { xs: 1.5, md: 2 },
            py: 1.25,
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 850 }}>
              Intake Hub
            </Typography>
            <Chip
              size="small"
              variant="soft"
              color={error ? 'warning' : 'success'}
              label={error ? 'Needs attention' : 'Admin'}
              sx={{ fontWeight: 800 }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 260 } }}>
              <InputLabel id="admin-intake-provider-scope-label">Scope</InputLabel>
              <Select
                labelId="admin-intake-provider-scope-label"
                label="Scope"
                value={selectedUserId}
                onChange={event => setSelectedUserId(event.target.value)}
              >
                <MenuItem value="">Organization scope</MenuItem>
                {providerOptions.map(provider => (
                  <MenuItem key={provider.id} value={provider.id}>
                    <Box>
                      <Typography variant="body2">{provider.label}</Typography>
                      {provider.email && (
                        <Typography variant="caption" color="text.secondary">
                          {provider.email}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button size="small" variant="outlined" onClick={() => handleOpenFilePicker()}>
              Upload files
            </Button>
            <Button size="small" variant="text" onClick={() => navigate('/admin-v2/intake-hub')}>
              Manage definitions
            </Button>
            <Button
              size="small"
              variant="text"
              startIcon={<RefreshOutlinedIcon />}
              onClick={() => void refetch()}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Box>

      {pendingUploads.length > 0 && (
        <Box
          sx={{
            position: 'sticky',
            top: 72,
            zIndex: theme.zIndex.appBar - 1,
            px: { xs: 1.5, md: 2 },
            pt: 2,
            pointerEvents: 'none',
          }}
        >
          <Stack spacing={0.75} sx={{ pointerEvents: 'auto' }}>
            {pendingUploads.map(upload => (
              <PendingUploadCard
                key={upload.artifactId}
                filename={upload.filename}
                status={upload.status}
                documentReferences={upload.documentReferences}
                extractionSignals={upload.extractionSignals}
              />
            ))}
          </Stack>
        </Box>
      )}

      {hasNoEnrollments ? (
        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6">Start intake for {scopeLabel}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Select service enrollments, then Intake Hub will create the required items for
                    this organization or provider scope.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {serviceOptions.map(service => (
                    <Chip
                      key={service.id}
                      clickable
                      color={selectedServiceIds.includes(service.id) ? 'primary' : 'default'}
                      variant={selectedServiceIds.includes(service.id) ? 'filled' : 'outlined'}
                      label={service.label}
                      onClick={() => handleToggleService(service.id)}
                    />
                  ))}
                </Stack>
                <Box>
                  <Button
                    variant="contained"
                    disabled={isEnrolling || isFetching || selectedServiceIds.length === 0}
                    onClick={() => void handleStartIntake()}
                  >
                    Start intake
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      ) : (
        <IntakeHubDashboard
          scenario={scenario}
          documentInventory={documentInventory?.documents ?? []}
          documentAccess={{
            scope: 'admin',
            organizationId,
            userId: selectedUserId || undefined,
          }}
          onUploadRequest={handleOpenFilePicker}
        />
      )}
    </Box>
  );
}
